import Calendar from 'react-calendar';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Room, RoomTypeAvailability } from '../../types/accommodation';
import { fetchAvailabilities } from '../../utils/functions';
import Spinner from '../../components/Spinner';

interface Props {
  activeRoom: Room;
  calendarTopElements: (roomType: string) => JSX.Element;
  isTabletOrMobile: boolean;
  dateFormatLanguage: string;
  calendarDateFormatOptions: Intl.DateTimeFormatOptions;
  startDate: Date;
  idToken: string;
}

function checkRoomTypeAvailability(
  params: { date: Date; view: string; activeStartDate: Date },
  availabilities: RoomTypeAvailability[] | undefined
) {
  let className = 'react-calendar__month-view__days__day';
  const { date, view } = params;

  if (availabilities && view === 'month') {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (today.getTime() > date.getTime()) {
      return className + '--past';
    }

    const roomTypeData = availabilities.find(
      (availability) =>
        new Date(availability.date).toDateString() === date.toDateString()
    );

    if (roomTypeData) {
      switch (roomTypeData.availabilityLevel) {
        case 'zero':
          className += '--blocked';
          break;
        case 'limited':
          className += '--limited';
          break;
        case 'minimum':
          className += '--minimum';
          break;
      }
    }
    return className;
  }
}

function RoomTypeCalendar(props: Props): JSX.Element {
  const [roomTypeAvailability, setRoomTypeAvailability] = useState<{
    [key: string]: RoomTypeAvailability[];
  }>();
  const [isLoading, setIsloading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>();

  const { t } = useTranslation('owned_rooms');

  useEffect(() => {
    async function getAvailabilities() {
      if (!props.activeRoom) {
        return;
      }
      setErrorMessage(null);
      setIsloading(true);
      const endDate = new Date(props.startDate.getTime());
      endDate.setFullYear(endDate.getFullYear() + 2);
      // Set to last day of month.
      endDate.setDate(0);
      const searchStartDate = new Date(props.startDate.getTime());
      const roomTypeData = await fetchAvailabilities({
        token: props.idToken,
        startDate: searchStartDate.toISOString().split('T')[0],
        endDate: endDate.toISOString().split('T')[0],
        roomTypeId: props.activeRoom.roomTypeId,
        resort: props.activeRoom.resort,
      });

      if (roomTypeData) {
        const roomType = roomTypeData.find(
          (data) => data.roomTypeId === parseInt(props.activeRoom?.roomTypeId)
        );
        if (roomType) {
          const roomTypeAvailabilityData = roomTypeAvailability ?? {};
          roomTypeAvailabilityData[props.activeRoom.roomId] =
            roomType.availability;
          setRoomTypeAvailability(roomTypeAvailabilityData);
        }
      } else {
        setErrorMessage(t("Couldn't find availability for the room type."));
      }
      setIsloading(false);
    }

    // Check if there's already data for the room type.
    if (
      props.activeRoom &&
      (!roomTypeAvailability || !roomTypeAvailability[props.activeRoom.roomId])
    ) {
      getAvailabilities();
    }
  }, [props.activeRoom]);

  return (
    <div>
      {isLoading && (
        <div className='my-12'>
          <Spinner />
        </div>
      )}

      {!isLoading && errorMessage && (
        <p className='text-red-500 my-20'>{errorMessage}</p>
      )}

      {!isLoading && roomTypeAvailability && !errorMessage && (
        <>
          {props.calendarTopElements('roomType')}

          <Calendar
            formatShortWeekday={(locale, date) =>
              date.toLocaleDateString(
                props.dateFormatLanguage,
                props.calendarDateFormatOptions
              )
            }
            activeStartDate={props.startDate}
            showNavigation={false}
            showWeekNumbers={true}
            view={'month'}
            tileDisabled={() => {
              return true;
            }}
            showDoubleView={!props.isTabletOrMobile}
            tileClassName={(params) =>
              checkRoomTypeAvailability(
                params,
                roomTypeAvailability[props.activeRoom.roomId]
              )
            }
          />
        </>
      )}
    </div>
  );
}

export default RoomTypeCalendar;
